import React, { useState } from 'react';
import { 
  Camera, 
  MapPin, 
  Heart,
  Tent,
  Bike,
  Ship,
  Flame,
  Shield,
  Users,
  CreditCard,
  Menu,
  X,
  HelpCircle
} from 'lucide-react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import AboutUs from './pages/AboutUs';
import HowItWorks from './pages/HowItWorks';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import NotFound from './pages/NotFound';
import SEO from './components/SEO';
import emailjs from '@emailjs/browser';
import { addToMailingList } from './services/mailchimp';
import { usePageTracking } from './utils/analytics';

function NavLink({ to, children }: { to: string; children: React.ReactNode }) {
  const location = useLocation();
  const isActive = location.pathname === to;
  
  return (
    <Link
      to={to}
      className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
        isActive 
          ? 'bg-green-600 text-white' 
          : 'text-gray-700 hover:bg-green-50'
      }`}
      aria-current={isActive ? 'page' : undefined}
    >
      {children}
    </Link>
  );
}

function Navigation() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 bg-white shadow-md z-50" aria-label="Main navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2" aria-label="GearLoop Home">
            <Tent className="w-8 h-8 text-green-600" aria-hidden="true" />
            <span className="text-xl font-bold text-gray-900">GearLoop</span>
          </Link>

          <div className="hidden md:flex items-center space-x-4">
            <NavLink to="/">Home</NavLink>
            <NavLink to="/about">About Us</NavLink>
            <NavLink to="/how-it-works">How It Works</NavLink>
            <NavLink to="/faq">FAQ</NavLink>
            <NavLink to="/contact">Contact</NavLink>
          </div>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
            aria-expanded={isOpen}
            aria-label="Toggle navigation menu"
          >
            {isOpen ? (
              <X className="w-6 h-6" aria-hidden="true" />
            ) : (
              <Menu className="w-6 h-6" aria-hidden="true" />
            )}
          </button>
        </div>

        {isOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link
                to="/"
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-green-50"
                onClick={() => setIsOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/about"
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-green-50"
                onClick={() => setIsOpen(false)}
              >
                About Us
              </Link>
              <Link
                to="/how-it-works"
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-green-50"
                onClick={() => setIsOpen(false)}
              >
                How It Works
              </Link>
              <Link
                to="/faq"
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-green-50"
                onClick={() => setIsOpen(false)}
              >
                FAQ
              </Link>
              <Link
                to="/contact"
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-green-50"
                onClick={() => setIsOpen(false)}
              >
                Contact
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

function HomePage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await addToMailingList(email, '');
      setSubmitted(true);
      setEmail('');
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to join waitlist. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO 
        title="GearLoop - Rent Outdoor Gear from Locals | Own Less, Explore More"
        description="Find and rent high-quality outdoor equipment from locals near you. Camping gear, bikes, water sports equipment and more. Join the outdoor sharing economy with GearLoop."
        keywords={[
          'rent outdoor gear',
          'local camping equipment',
          'bike rental near me',
          'water sports gear rental',
          'outdoor equipment sharing',
          'sustainable outdoor gear',
          'peer to peer rental',
          'adventure gear rental',
          'camping gear near me',
          'outdoor marketplace'
        ]}
      />
      <div className="min-h-screen bg-white pt-16">
        <div className="bg-green-600 text-white py-3" role="banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-center text-sm md:text-base font-medium">
              🚀 Coming Soon: GearLoop launches Summer 2025! Join the waitlist to get early access.
            </p>
          </div>
        </div>

        <div 
          className="relative h-screen flex items-center justify-center"
          style={{
            backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url("https://images.unsplash.com/photo-1517823382935-51bfcb0ec6bc?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
          role="main"
        >
          <div className="absolute inset-0 bg-black opacity-40"></div>
          <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
              Own Less, Explore More
              <span className="block text-2xl sm:text-3xl mt-2">Rent outdoor kit from locals. <br />Got extra equipment? Earn by renting it out!</span>
            </h1>
            <form onSubmit={handleSubmit} className="max-w-md mx-auto">
              <div className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 px-6 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                  disabled={isSubmitting}
                  aria-label="Email address"
                />
                <button
                  type="submit"
                  className={`px-6 py-3 bg-green-800 text-white rounded-lg hover:bg-green-900 transition duration-200 ${
                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Joining...' : 'Join Waitlist'}
                </button>
              </div>
              {error && (
                <p className="mt-2 text-red-400 text-sm" role="alert">{error}</p>
              )}
            </form>
          </div>
        </div>

        <div className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold text-center mb-16">How It Works</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              <div className="text-center">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Camera className="w-8 h-8 text-green-600" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-semibold mb-4">List Your Gear</h3>
                <p className="text-gray-600">Snap a photo and set your price.</p>
              </div>
              <div className="text-center">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <MapPin className="w-8 h-8 text-green-600" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Rent from Locals</h3>
                <p className="text-gray-600">Book gear near you, hassle-free.</p>
              </div>
              <div className="text-center">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Heart className="w-8 h-8 text-green-600" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Explore More, Own Less</h3>
                <p className="text-gray-600">Get quality gear without the high cost.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold text-center mb-16">Why Use GearLoop?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
              <div className="text-center">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CreditCard className="w-8 h-8 text-blue-600" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Save Money</h3>
                <p className="text-gray-600">Up to 70% cheaper than buying new gear</p>
              </div>
              <div className="text-center">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Shield className="w-8 h-8 text-green-600" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Reduce Waste</h3>
                <p className="text-gray-600">Join the sharing economy movement</p>
              </div>
              <div className="text-center">
                <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="w-8 h-8 text-orange-600" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Support Local</h3>
                <p className="text-gray-600">Connect with fellow adventurers</p>
              </div>
            </div>
          </div>
        </div>

        <div className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold text-center mb-16">Popular Gear Categories</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="relative rounded-xl overflow-hidden group">
                <img 
                  src="https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80" 
                  alt="Camping" 
                  className="w-full h-64 object-cover group-hover:scale-110 transition duration-300"
                />
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                  <div className="text-center">
                    <Tent className="w-12 h-12 text-white mx-auto mb-2" aria-hidden="true" />
                    <h3 className="text-xl font-bold text-white">Camping Gear</h3>
                  </div>
                </div>
              </div>
              <div className="relative rounded-xl overflow-hidden group">
                <img 
                  src="https://images.unsplash.com/photo-1511994298241-608e28f14fde?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80" 
                  alt="Biking" 
                  className="w-full h-64 object-cover group-hover:scale-110 transition duration-300"
                />
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                  <div className="text-center">
                    <Bike className="w-12 h-12 text-white mx-auto mb-2" aria-hidden="true" />
                    <h3 className="text-xl font-bold text-white">Bikes</h3>
                  </div>
                </div>
              </div>
              <div className="relative rounded-xl overflow-hidden group">
                <img 
                  src="https://images.unsplash.com/photo-1519789110007-0e751882be76?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80" 
                  alt="Water Sports" 
                  className="w-full h-64 object-cover group-hover:scale-110 transition duration-300"
                />
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                  <div className="text-center">
                    <Ship className="w-12 h-12 text-white mx-auto mb-2" aria-hidden="true" />
                    <h3 className="text-xl font-bold text-white">Water Sports</h3>
                  </div>
                </div>
              </div>
              <div className="relative rounded-xl overflow-hidden group">
                <img 
                  src="https://images.unsplash.com/photo-1523987355523-c7b5b0dd90a7?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80" 
                  alt="Camping Accessories" 
                  className="w-full h-64 object-cover group-hover:scale-110 transition duration-300"
                />
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                  <div className="text-center">
                    <Flame className="w-12 h-12 text-white mx-auto mb-2" aria-hidden="true" />
                    <h3 className="text-xl font-bold text-white">Camping Accessories</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="py-20 bg-green-800 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-8">Be the First to Access GearLoop</h2>
            <form onSubmit={handleSubmit} className="max-w-md mx-auto">
              <div className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 px-6 py-3 rounded-lg text-black focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                  disabled={isSubmitting}
                  aria-label="Email address for early access"
                />
                <button
                  type="submit"
                  className={`px-6 py-3 bg-white text-green-800 rounded-lg hover:bg-gray-100 transition duration-200 ${
                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Signing Up...' : 'Sign Up Now'}
                </button>
              </div>
              {error && (
                <p className="mt-2 text-red-400 text-sm" role="alert">{error}</p>
              )}
            </form>
          </div>
        </div>

        <footer className="bg-gray-900 text-gray-300 py-12" role="contentinfo">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-white text-lg font-semibold mb-4">GearLoop</h3>
                <p className="text-sm">Connecting outdoor enthusiasts with the gear they need.</p>
              </div>
              <div>
                <h4 className="text-white text-lg font-semibold mb-4">Quick Links</h4>
                <ul className="space-y-2">
                  <li><Link to="/about" className="hover:text-white transition">About Us</Link></li>
                  <li><Link to="/how-it-works" className="hover:text-white transition">How It Works</Link></li>
                  <li><Link to="/faq" className="hover:text-white transition">FAQ</Link></li>
                  <li><Link to="/contact" className="hover:text-white transition">Contact</Link></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-800 mt-12 pt-8 text-center text-sm">
              <p>&copy; {new Date().getFullYear()} GearLoop. All rights reserved.</p>
            </div>
          </div>
        </footer>

        {submitted && (
          <div 
            className="fixed bottom-4 right-4 bg-green-800 text-white px-6 py-3 rounded-lg shadow-lg animate-fade-in-up"
            role="alert"
            aria-live="polite"
          >
            Thanks for joining! We'll keep you updated on our launch.
          </div>
        )}
      </div>
    </>
  );
}

function AppContent() {
  usePageTracking();

  return (
    <>
      <Navigation />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;